/**
 * StaffLogin — court-booking-style staff sign-in, in MUI Minimal + Claude rust.
 *
 * Flow (shared-terminal friendly, Rajat's call 2026-07-01):
 *   Step 1: Enter the venue staff-login code + a simple math security-check; optionally remember
 *           this device (skips step 1 next time).
 *   Step 2: Tap your name from the venue's staff list.
 *   Step 3: Enter your PIN on the on-screen numeric keypad (auto-submits at 6 digits).
 *
 * On success the staff JWT is stored under the same ACCESS_TOKEN key the owner login uses, plus
 * AUTH_USER carries { id, name, role, venueId } so can() gates the app identically to the owner.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import {
    Box, Card, Typography, Stack, TextField, Button, Checkbox, FormControlLabel,
    Link, Divider, Chip, Avatar, IconButton, InputAdornment,
} from '@mui/material';
import { AiOutlineArrowLeft } from 'react-icons/ai';
import ThemeConfig from '../../minimal/theme';
import API from '../../api/Api';
import LocalStorage from '../../components/LocalStorage';
import { can } from '../../constants/permissions';
import { SidebarData } from '../../components/layout/Owner/SidebarData';
import logo from '../../assets/logo.png';

/** Key used to remember the venue code on shared devices */
const STAFF_CODE_KEY = 'cometqr_staff_code';

/** Role badge colours (MUI Chip) — manager = rust primary */
const ROLE_COLOR = { manager: 'primary', server: 'success', kitchen: 'warning' };

const RoleChip = ({ role, t }) => (
    <Chip size="small" color={ROLE_COLOR[role] || 'default'} variant="filled"
        label={t(`settings.staff_role_${role}`, role)} sx={{ mt: 0.5, color: '#fff' }} />
);

/** A fresh a+b challenge (1–9 each) so the front door isn't trivially botted. */
const newChallenge = () => ({ a: 1 + Math.floor(Math.random() * 8), b: 1 + Math.floor(Math.random() * 8) });

/* ─────────────────────────────────────────────────────────────────────────────
   On-screen numeric keypad + PIN dots
───────────────────────────────────────────────────────────────────────────── */
const PinDots = ({ length, filled }) => (
    <Stack direction="row" spacing={1.5} justifyContent="center" sx={{ my: 2 }}>
        {Array.from({ length }).map((_, i) => (
            <Box key={i} sx={{
                width: 14, height: 14, borderRadius: '50%',
                border: '2px solid', borderColor: 'primary.main',
                bgcolor: i < filled ? 'primary.main' : 'transparent',
                transition: 'background-color .12s',
            }} />
        ))}
    </Stack>
);

const PinPad = ({ pin, onChange, disabled }) => {
    const keys = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '', '0', '⌫'];
    const press = (d) => {
        if (disabled) return;
        if (d === '⌫') onChange(pin.slice(0, -1));
        else if (d !== '' && pin.length < 6) onChange(pin + d);
    };
    return (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1, maxWidth: 260, mx: 'auto' }}>
            {keys.map((d, i) => (
                <Button key={i} type="button" onClick={() => press(d)}
                    disabled={disabled || d === ''}
                    variant={d === '⌫' ? 'text' : 'outlined'} color="inherit"
                    sx={{
                        height: 54, fontSize: 20, fontWeight: 600, borderRadius: 1.5,
                        color: 'text.primary', borderColor: 'grey.300',
                        visibility: d === '' ? 'hidden' : 'visible',
                        '&:hover': { borderColor: 'primary.main', bgcolor: 'primary.lighter' },
                    }}>
                    {d}
                </Button>
            ))}
        </Box>
    );
};

/* ─────────────────────────────────────────────────────────────────────────────
   Main component
───────────────────────────────────────────────────────────────────────────── */
const StaffLogin = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const remembered = (typeof window !== 'undefined' && localStorage.getItem(STAFF_CODE_KEY)) || '';

    // Step 1 — venue code + security check
    const [code, setCode] = useState(remembered);
    const [codeInput, setCodeInput] = useState(remembered);
    const [rememberDevice, setRememberDevice] = useState(true);
    const [challenge, setChallenge] = useState(newChallenge);
    const [mathInput, setMathInput] = useState('');
    const [step, setStep] = useState(remembered ? 2 : 1);

    // Step 2 — staff list + selection
    const [staffList, setStaffList] = useState([]);
    const [loadingList, setLoadingList] = useState(false);
    const [selectedStaff, setSelectedStaff] = useState(null);

    // Step 3 — PIN
    const [pin, setPin] = useState('');
    const [loggingIn, setLoggingIn] = useState(false);

    /* Load the venue's staff once a code is confirmed */
    const loadStaff = useCallback((venueCode) => {
        setLoadingList(true);
        setStaffList([]);
        setSelectedStaff(null);
        API.getData('staff/list-for-login', { code: venueCode })
            .then((res) => {
                const list = res.data?.data || [];
                if (!list.length) {
                    toast.error(t('staff_login_no_staff', 'No active staff found for that code.'));
                    setStep(1);
                    return;
                }
                setStaffList(list);
            })
            .catch(() => {
                toast.error(t('staff_login_code_invalid', 'Invalid venue code. Please try again.'));
                setStep(1);
            })
            .finally(() => setLoadingList(false));
    }, [t]);

    useEffect(() => {
        if (remembered) loadStaff(remembered);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    /* Step 1 submit — validate the math check, then load staff */
    const handleCodeSubmit = (e) => {
        e.preventDefault();
        const trimmed = codeInput.trim().toUpperCase();
        if (!trimmed) return;
        if (Number(mathInput) !== challenge.a + challenge.b) {
            toast.error(t('staff_login_math_wrong', 'Security check is incorrect — please try again.'));
            setChallenge(newChallenge());
            setMathInput('');
            return;
        }
        if (rememberDevice) localStorage.setItem(STAFF_CODE_KEY, trimmed);
        else localStorage.removeItem(STAFF_CODE_KEY);
        setCode(trimmed);
        setStep(2);
        loadStaff(trimmed);
    };

    const handleChangeVenue = () => {
        localStorage.removeItem(STAFF_CODE_KEY);
        setCode(''); setCodeInput(''); setStaffList([]); setSelectedStaff(null); setPin('');
        setChallenge(newChallenge()); setMathInput('');
        setStep(1);
    };

    const handleSelectStaff = (member) => { setSelectedStaff(member); setPin(''); };

    const handlePinSubmit = async (e) => {
        e?.preventDefault();
        if (pin.length < 4) {
            toast.error(t('settings.staff_pin_invalid', 'PIN must be 4–6 digits.'));
            return;
        }
        setLoggingIn(true);
        try {
            const res = await API.postData('staff/login', { code, staff_id: selectedStaff.id, pin });
            const { token, staff } = res.data?.data || {};
            if (!token || !staff) throw new Error('bad response');

            let venueId = null;
            try {
                const payload = JSON.parse(atob(token.split('.')[1]));
                venueId = payload.venueId || null;
            } catch (err) { /* ignore — venueId stays null */ }

            const identity = {
                id: staff.id, name: staff.name, role: staff.role, venueId,
                Venue: { id: venueId },
                type_id: 3,
                is_staff: true,
            };
            LocalStorage.persistLogin(token, 720, identity);

            const firstAllowed = SidebarData.find((item) => can(staff.role, item.perm));
            const destination = firstAllowed ? firstAllowed.path : '/owner/dashboard';
            toast.success(t('staff_login_welcome', 'Welcome, {{name}}!', { name: staff.name }));
            window.location.replace(destination);
        } catch (err) {
            const msg = err?.response?.data?.message;
            toast.error(msg || t('staff_login_pin_error', 'Incorrect PIN. Please try again.'));
            setPin('');
        } finally {
            setLoggingIn(false);
        }
    };

    /* Auto-submit once the full 6-digit PIN is entered */
    useEffect(() => {
        if (pin.length === 6 && selectedStaff && !loggingIn) handlePinSubmit();
    }, [pin]); // eslint-disable-line react-hooks/exhaustive-deps

    const inPin = step === 2 && selectedStaff;

    return (
        <ThemeConfig>
            <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'background.default', p: 2 }}>
                <Card sx={{ width: '100%', maxWidth: 420, p: { xs: 3, sm: 4 }, boxShadow: (theme) => theme.customShadows.z24 }}>
                    <Box sx={{ textAlign: 'center', mb: 3 }}>
                        <img src={logo} alt="CometQR" style={{ height: 48, width: 'auto' }} />
                        <Typography variant="h5" sx={{ mt: 1.5 }}>{t('staff_login_title', 'Staff sign-in')}</Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                            {t('staff_login_subtitle', 'Clock in to take orders and manage the floor.')}
                        </Typography>
                    </Box>

                    {/* ── STEP 1 — venue code + security check ─────────────────── */}
                    {step === 1 && (
                        <form onSubmit={handleCodeSubmit} autoComplete="off">
                            <Stack spacing={2.5}>
                                <TextField
                                    fullWidth autoFocus label={t('staff_login_code_label', 'Venue code')}
                                    value={codeInput} onChange={(e) => setCodeInput(e.target.value.toUpperCase())}
                                    placeholder={t('staff_login_code_placeholder', 'e.g. ABC123')}
                                    inputProps={{ maxLength: 16 }}
                                    helperText={t('staff_login_code_hint', 'Ask your manager for the venue login code.')}
                                />
                                <TextField
                                    fullWidth type="number" label={t('staff_login_math_label', 'Security check')}
                                    value={mathInput} onChange={(e) => setMathInput(e.target.value)}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{challenge.a} + {challenge.b} =</Typography>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                                <FormControlLabel
                                    control={<Checkbox checked={rememberDevice} onChange={(e) => setRememberDevice(e.target.checked)} />}
                                    label={t('staff_login_remember', 'Remember this device')}
                                />
                                <Button type="submit" fullWidth size="large" variant="contained"
                                    disabled={!codeInput.trim() || !mathInput || loadingList}>
                                    {loadingList ? t('common.loading', 'Loading…') : t('staff_login_next', 'Next')}
                                </Button>
                            </Stack>
                        </form>
                    )}

                    {/* ── STEP 2 — pick your name ───────────────────────────────── */}
                    {step === 2 && !selectedStaff && (
                        <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                                <Typography variant="body2" color="text.secondary">
                                    {t('staff_login_code_label', 'Venue code')}: <strong>{code}</strong>
                                </Typography>
                                <Link component="button" type="button" variant="body2" underline="hover" onClick={handleChangeVenue}>
                                    {t('staff_login_change_venue', 'Change venue')}
                                </Link>
                            </Box>
                            {loadingList ? (
                                <Typography align="center" color="text.secondary" sx={{ py: 3 }}>{t('common.loading', 'Loading…')}</Typography>
                            ) : (
                                <>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                                        {t('staff_login_pick', "Who's clocking in?")}
                                    </Typography>
                                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(104px, 1fr))', gap: 1.5 }}>
                                        {staffList.map((member) => (
                                            <Box key={member.id} onClick={() => handleSelectStaff(member)}
                                                sx={{
                                                    border: '1px solid', borderColor: 'grey.300', borderRadius: 1.5,
                                                    p: 1.5, textAlign: 'center', cursor: 'pointer', transition: 'all .15s',
                                                    '&:hover': { borderColor: 'primary.main', bgcolor: 'primary.lighter' },
                                                }}>
                                                <Avatar sx={{ width: 44, height: 44, mx: 'auto', bgcolor: 'primary.main', fontWeight: 600 }}>
                                                    {member.name.charAt(0).toUpperCase()}
                                                </Avatar>
                                                <Typography variant="subtitle2" sx={{ mt: 1, lineHeight: 1.2 }} noWrap>{member.name}</Typography>
                                                <RoleChip role={member.role} t={t} />
                                            </Box>
                                        ))}
                                    </Box>
                                </>
                            )}
                        </Box>
                    )}

                    {/* ── STEP 3 — PIN keypad ───────────────────────────────────── */}
                    {inPin && (
                        <Box sx={{ textAlign: 'center' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', mb: 1 }}>
                                <Button size="small" color="inherit" startIcon={<AiOutlineArrowLeft />}
                                    onClick={() => { setSelectedStaff(null); setPin(''); }} sx={{ color: 'text.secondary' }}>
                                    {t('staff_login_back_to_list', 'Back')}
                                </Button>
                            </Box>
                            <Avatar sx={{ width: 52, height: 52, mx: 'auto', bgcolor: 'primary.main', fontWeight: 600, fontSize: 22 }}>
                                {selectedStaff.name.charAt(0).toUpperCase()}
                            </Avatar>
                            <Typography variant="h6" sx={{ mt: 1 }}>{selectedStaff.name}</Typography>
                            <RoleChip role={selectedStaff.role} t={t} />
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                {t('staff_login_enter_pin', 'Enter your PIN')}
                            </Typography>

                            <PinDots length={6} filled={pin.length} />
                            <PinPad pin={pin} onChange={setPin} disabled={loggingIn} />

                            {pin.length >= 4 && pin.length < 6 && (
                                <Button fullWidth size="large" variant="contained" onClick={handlePinSubmit}
                                    disabled={loggingIn} sx={{ mt: 2.5 }}>
                                    {loggingIn ? t('common.loading', 'Loading…') : t('staff_login_sign_in', 'Sign in')}
                                </Button>
                            )}
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
                                {t('staff_login_first_time', 'First time, or forgot your PIN? Ask your manager to set it in Staff settings.')}
                            </Typography>
                        </Box>
                    )}

                    <Divider sx={{ my: 2.5 }} />
                    <Typography variant="body2" align="center">
                        <Link href="/login" underline="hover" sx={{ color: 'text.secondary' }}>
                            {t('staff_login_owner_link', 'Owner / Manager sign-in')}
                        </Link>
                    </Typography>
                </Card>
            </Box>
        </ThemeConfig>
    );
};

export default StaffLogin;
